// Modal overlay shown while the auto-fixer rebuilds an archive. The
// overlay carries a title, an indeterminate progress bar (driven by
// CSS keyframes), and a rotating Minecraft-themed quip pulled from
// `fix-quips.ts`. `runFix` opens the overlay before calling fixZip
// and closes it in a finally block so the modal never leaks if the
// fixer throws.

import { FIX_QUIPS } from "./fix-quips.js";

export interface FixOverlayHandle {
  /** Tear the overlay down and stop the quip rotation. Idempotent. */
  close(): void;
}

const QUIP_ROTATE_MS = 1500;

export function showFixOverlay(): FixOverlayHandle {
  const existing = document.querySelector<HTMLElement>(".fix-overlay-backdrop");
  if (existing) {
    return { close: () => existing.remove() };
  }

  const backdrop = document.createElement("div");
  backdrop.className = "fix-overlay-backdrop";

  const card = document.createElement("div");
  card.className = "fix-overlay";
  card.setAttribute("role", "dialog");
  card.setAttribute("aria-modal", "true");
  card.setAttribute("aria-label", "Auto-fixing your package");

  const title = document.createElement("h3");
  title.textContent = "Auto-fixing your package";

  const quip = document.createElement("p");
  quip.className = "fix-overlay-quip";
  quip.setAttribute("aria-live", "polite");
  quip.textContent = pickQuip();

  const bar = document.createElement("div");
  bar.className = "fix-overlay-bar";
  bar.setAttribute("role", "progressbar");
  bar.setAttribute("aria-label", "Auto-fixer in progress");
  const fill = document.createElement("span");
  bar.appendChild(fill);

  card.append(title, quip, bar);
  backdrop.append(card);
  document.body.append(backdrop);

  // Track the previous quip so consecutive rotations never repeat
  // (with N >= 2 entries this stays well-defined; we already loaded
  // a list of dozens, so the loop is effectively a one-shot).
  let lastQuip = quip.textContent ?? "";
  const interval = window.setInterval(() => {
    let next = pickQuip();
    if (FIX_QUIPS.length > 1) {
      let attempts = 0;
      while (next === lastQuip && attempts < 8) {
        next = pickQuip();
        attempts += 1;
      }
    }
    lastQuip = next;
    quip.textContent = next;
  }, QUIP_ROTATE_MS);

  let closed = false;
  return {
    close() {
      if (closed) return;
      closed = true;
      window.clearInterval(interval);
      backdrop.remove();
    },
  };
}

function pickQuip(): string {
  if (FIX_QUIPS.length === 0) return "Working...";
  const i = Math.floor(Math.random() * FIX_QUIPS.length);
  return FIX_QUIPS[i] ?? "Working...";
}
